import {get} from 'lodash-es'
import i18n from 'i18next'

import {format, addHours, addMinutes} from 'date-fns'
import {
  de,
  enUS as en,
  es,
  fr,
  it,
  ja,
  ko,
  nl,
  pl,
  pt,
  ru,
  sv,
  tr,
  ar,
  fi,
  nb,
  hu,
  zhCN as zh,
  da,
  el,
  bg,
  ro,
} from 'date-fns/locale'

import timeZones from './timeZones.jsx'

const locales = {
  de,
  en,
  es,
  fr,
  it,
  ja,
  ko,
  nl,
  pl,
  pt,
  ru,
  sv,
  tr,
  ar,
  fi,
  nb,
  hu,
  zh,
  da,
  el,
  ro,
  bg,
}

const now = new Date()

export const buildRangeArray = (items) =>
  Array.apply(null, {length: items}).map(Number.call, Number)

export const chunk = (array, size) => {
  const firstChunk = array.slice(0, size)

  if (!firstChunk.length) return array
  else return [firstChunk].concat(chunk(array.slice(size, array.length), size))
}

export const getQuoteCode = (quotes) =>
  quotes.reduce(
    (quoteCode, quote) => (quote && quote.code ? quote.code : quoteCode),
    '',
  )

export const getQuoteCodeFromUrl = () => {
  const quoteCodeHashPrefix = '#quote-code='

  if (window.location.hash.indexOf(quoteCodeHashPrefix) > -1)
    return window.location.hash.replace(quoteCodeHashPrefix, '')
  else return ''
}

export const getLanguageFromLanguages = (languageId, languages) =>
  languages.find((language) => language.id === languageId)

// Helper function to extract base language code from locale
export const getBaseLanguageCode = (locale) => {
  if (!locale) return 'en'
  // Extract the base language code (e.g., 'en' from 'en-US', 'it' from 'it-IT')
  return locale.split('-')[0].toLowerCase()
}

const currencySymbols = {
  AUD: '$',
  BGN: 'лв',
  BRL: 'R$',
  CAD: '$',
  CNY: '¥',
  EUR: '€',
  HRK: 'kn',
  CZK: 'Kč',
  DKK: 'kr',
  HKD: '$',
  HUF: 'Ft',
  ISK: 'kr',
  INR: 'inr',
  IDR: 'Rp',
  ILS: '₪',
  JPY: '¥',
  KRW: '₩',
  MYR: 'RM',
  MXN: '$',
  NZD: '$',
  NOK: 'kr',
  PHP: '₱',
  PLN: 'zł',
  RON: 'lei',
  RUB: '₽',
  SGD: '$',
  ZAR: 'R',
  SEK: 'kr',
  CHF: 'CHF',
  THB: '฿',
  TRY: 'try',
  GBP: '£',
  USD: '$',
}

export const currencyToSymbol = (currency) =>
  currencySymbols[currency] || currency

export const dateFnsLocalizedFormat = (date, formatString) => {
  return format(date, formatString, {
    locale: locales[i18n.language] || en,
  })
}

export const currencyValueLocalizedFormat = (value, options) =>
  value.toLocaleString(i18n.language, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    ...options,
  })

export const dateFnsLocalizedTimeFormat = (date) => {
  const is12HoursFormat = now.toLocaleString(i18n.language).match(/am|pm/i)
  return format(date, is12HoursFormat ? 'hh:mm a' : 'HH:mm', {
    locale: locales[i18n.language] || en
  })
}

export const dateFnsLocalizedTimeFormatInTimezone = (date, timeZone) => {
  const is12HoursFormat = now.toLocaleString(i18n.language).match(/am|pm/i)
  const timeInTimezone = convertTimeToTimezone(date, timeZone)
  return format(timeInTimezone, is12HoursFormat ? 'hh:mm a' : 'HH:mm', {
    locale: locales[i18n.language] || en
  })
}

export const toDoubleDigits = (number) =>
  number > 9 ? `${number}` : `0${number}`

export const getBrowserTimeZone = () => {
  const timezoneOffset = -now.getTimezoneOffset()
  const timezoneOffsetInHours = timezoneOffset / 60
  const timezoneOffsetHH = parseInt(timezoneOffsetInHours)
  const timezoneOffsetMM = Math.abs(
    parseInt(timezoneOffsetHH * 60 - timezoneOffset),
  )
  const timeZoneId = `${
    timezoneOffsetInHours >= 0 ? '+' : ''
  }${timezoneOffsetInHours}`
  const utcLabel = `(UTC ${timezoneOffsetHH >= 0 ? '+' : ''}${toDoubleDigits(
    timezoneOffsetHH,
  )}:${toDoubleDigits(timezoneOffsetMM)})`
  const matchedTimezone = timeZones.find(
    (timeZone) => timeZone.id === timeZoneId,
  )

  return {
    id: timeZoneId,
    name: utcLabel,
    label: matchedTimezone ? matchedTimezone.label : utcLabel,
  }
}

//fix convertTimeToTimezone function... oh my god....
export const convertDateWithTimezone = (selectedTime, timeZone) =>
  new Date(
    selectedTime.getTime() +
      60000 * (selectedTime.getTimezoneOffset() - +timeZone.id * -60),
  )

export const convertTimeToTimezone = (selectedTime, timeZone) => {
  const browserDeltaHours = parseInt(now.getTimezoneOffset() / 60)
  const browserDeltaMinutes = parseInt(
    browserDeltaHours * 60 - now.getTimezoneOffset(),
  )
  const timeZoneDelta = parseFloat(timeZone.id)
  const timeZoneDeltaHours = parseInt(timeZoneDelta) + browserDeltaHours
  const timeZoneDeltaMinutes = (timeZoneDelta % 1) * 60 + browserDeltaMinutes

  const selectedTimeTimeZoned = new Date(selectedTime)
  const selectedTimeTimeZonedHoursUpdated = addHours(
    selectedTimeTimeZoned,
    timeZoneDeltaHours * -1,
  )
  const selectedTimeTimeZonedHoursMinutesUpdated = addMinutes(
    selectedTimeTimeZonedHoursUpdated,
    timeZoneDeltaMinutes * -1,
  )
  return selectedTimeTimeZonedHoursMinutesUpdated
}

export const findNearTimeZone = (timeZoneString) => {
  const timeZoneStringValue = parseFloat(
    `${timeZoneString.slice(0, 1)}${parseInt(
      timeZoneString.slice(1, 3),
    )}.${parseInt((parseFloat(timeZoneString.slice(4, 6)) / 60) * 100)}`,
  )
  const timeZone = timeZones.find(
    (timeZoneOption) => parseFloat(timeZoneOption.id) === timeZoneStringValue,
  )

  if (timeZone) {
    return timeZone
  } else {
    return timeZones.sort((timeZoneOptionA, timeZoneOptionB) => {
      const timeZoneOptionATime = parseFloat(timeZoneOptionA.id)
      const timeZoneOptionBTime = parseFloat(timeZoneOptionB.id)
      return Math.abs(timeZoneStringValue - timeZoneOptionATime) <
        Math.abs(timeZoneStringValue - timeZoneOptionBTime)
        ? -1
        : 1
    })[0]
  }
}

export const timeZoneUtcString = (timeZone) => {
  const utcString = timeZone.name.match(/\((.*?)\)/)[1]
  if (utcString) return `(${utcString})`
  else return timeZone.name
}

export const timeZoneToLabel = (timeZone) => {
  if (timeZone.label) return timeZone.label
  else return timeZoneUtcString(timeZone)
}

export const isEmailValid = (emailAddress) =>
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/i.test(
    emailAddress,
  ) //eslint-disable-line

export const isProjectNameValid = (projectName) =>
  /^[\u0020-\u007e\u00a0-\u00ff]+$/i.test(projectName) || !projectName.length

export const decodeJwt = (token) => {
  const base64Url = token.split('.')[1]
  if (base64Url) {
    const base64 = base64Url.replace('-', '+').replace('_', '/')
    return JSON.parse(window.atob(base64))
  } else return undefined
}

// Function to load most popular languages for current language
export const loadMostPopularLanguages = async (currentLanguage = 'it') => {
  try {
    // Import the most popular languages file for the current language
    const mostPopularData = await import(`../data/${currentLanguage}/most-popular-languages.json`)
    return mostPopularData.default?.mostPopularTargetLanguages || []
  } catch (error) {
    // Fallback to default popular languages
    return ['it-IT', 'es-ES', 'fr-FR', 'de-DE']
  }
}

export const isMostPopularLanguage = (language) => {
  const mostPopularLanguages = window.mostPopularTargetLanguages || []
  if (mostPopularLanguages.length > 0)
    return mostPopularLanguages.indexOf(language.id) > -1
  else return false
}

export const isValidOption = (option) => !!option.id && option.id.length > 0

export const isString = (stringToCheck) =>
  typeof stringToCheck === 'string' || stringToCheck instanceof String

export const randomId = (prefix = 'id-') =>
  `${prefix}${Math.random().toString(36).substr(2, 16)}`

export const apiErrorToMessage = (error) => {
  const errorCode = get(error, 'response.data.error')
  const errorMessageI18nKey = `ApiErrorMessages.${errorCode}`

  if (i18n.exists(errorMessageI18nKey)) return i18n.t(errorMessageI18nKey)
  else return i18n.t('Commons.genericApiError')
}

export const sortByOptionName = (optionA, optionB) =>
  optionA.name.localeCompare(optionB.name)

export const getEnvironmentFromDomain = () => {
  const hostname = window.location.hostname;

  if (hostname.includes('staging.translated.com') ||
      hostname.includes('localhost') ||
      hostname.includes('127.0.0.1') ||
      hostname.includes('staging.') ||
      hostname.includes('dev.') ||
      hostname.includes('test.')) {
    return 'staging';
  }

  return 'production';
}

export const getApiUrl = () => {
  const environment = getEnvironmentFromDomain();
  
  if (environment === 'staging') {
    return 'https://webapi.staging.translated.com';
  }
  
  return 'https://webapi.translated.com';
}

export const getFrontendUrl = () => {
  const environment = getEnvironmentFromDomain();
  
  if (environment === 'staging') {
    return 'https://staging.translated.com';
  }
  
  return 'https://translated.com';
}

export const getHubspotApiUrl = () => {
  const environment = getEnvironmentFromDomain();
  
  if (environment === 'staging') {
    return 'https://hb_proxy.staging.translated.com';
  }
  
  return 'https://hb_proxy.translated.com';
}

export const getMarketingQuoteKindByVariant = (variant) => {
  switch (variant) {
    case 'instant':
      return 'Lara Instant'
    case 'lara_tool':
      return 'Lara Tool'
    case 'pro':
      return 'Professional'
    default:
      return undefined
  }
}

export const customerTypeToIsCompany = (customerType) => {
  if (!customerType) return undefined
  const result = customerType === 'COMPANY' || customerType === 'SMB'
  return result
}

export const getEffectiveVatRate = (userVatRate, defaultVatRate) => {
  if (userVatRate !== undefined && userVatRate !== null) {
    const numericRate = parseFloat(userVatRate)
    return isNaN(numericRate) ? defaultVatRate : numericRate
  }
  return defaultVatRate
}

export const formatDeliveryDateString = (deliveryDate, timeZone) => {
  const date = convertDateWithTimezone(new Date(deliveryDate), timeZone)
  return `${dateFnsLocalizedFormat(
    date,
    'EEE dd MMM',
  )} ${dateFnsLocalizedTimeFormat(date)}`
}

export const formatDeliveryDateTimeZoneString = (timeZone) => {
  return timeZoneToLabel(timeZone)
}
